import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { apiInstance, Dispatch, GET_SAP_FAILED_PROVISIONS, PROVISION_ELECTRONIC_DOCS } from 'shared';
import Swal from 'sweetalert2';
import { Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const PROVISIONED_SUCCESS = 'DOCUMENT_PROVISIONED';

const PROVISION_ERROR_MESSAGES: Record<string, string> = {
  DOCUMENT_NOT_PROVISIONED: 'Documento no provisionado',
  DOCUMENT_ALREADY_PROVISIONED: 'El documento ya fue provisionado',
};

export interface SapFailedProvision {
  documentNumber: string;
  providerName: string;
  provisionValue: number;
  transactionType: string;
  provisionType: string;
}

export const useSeguimientoCuentas = () => {
  const dispatch = useDispatch<Dispatch>();

  const [content, setContent] = useState<SapFailedProvision[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [numberPage, setNumberPage] = useState(1);
  const [size, setSize] = useState(20);
  const [contentSize, setContentSize] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<SapFailedProvision[]>([]);

  const valuesResultsPages = [20, 50, 100, 250];

  const fetchData = async (page = 0, pageSize = size) => {
    setIsLoading(true);
    try {
      const response = await apiInstance.get(GET_SAP_FAILED_PROVISIONS, {
        params: { page, size: pageSize },
      });
      const data = response.data;
      const fetchedContent = data.content ?? [];
      setContent(fetchedContent);
      setTotalElements(Math.max(data.totalElements ?? 0, data.contentSize ?? 0, fetchedContent.length));
      setNumberPage(page + 1);
      setSize(data.size ?? pageSize);
      setContentSize(data.contentSize ?? fetchedContent.length);
    } catch {
      setContent([]);
      setTotalElements(0);
      setContentSize(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (_event: React.ChangeEvent<unknown>, value: number) => {
    setSelectedRows([]);
    fetchData(value - 1, size);
  };

  const handleChangeSizeRows = (event: any) => {
    const newSize = Number(event.target.value);
    setSize(newSize);
    setSelectedRows([]);
    fetchData(0, newSize);
  };

  const MAX_SELECTION = 20;

  const handleChangeCheck = (
    event: React.ChangeEvent<HTMLInputElement>,
    row: SapFailedProvision,
  ) => {
    if (event.target.checked) {
      if (selectedRows.length >= MAX_SELECTION) {
        Swal.fire({
          title: 'Límite alcanzado',
          text: `Solo se pueden seleccionar máximo ${MAX_SELECTION} documentos a la vez.`,
          icon: 'warning',
          timer: 3000,
          showConfirmButton: false,
        });
        return;
      }
      setSelectedRows(prev => [...prev, row]);
    } else {
      setSelectedRows(prev =>
        prev.filter(r => !(r.documentNumber === row.documentNumber && r.transactionType === row.transactionType && r.provisionType === row.provisionType)),
      );
    }
  };

  const clearSelection = () => setSelectedRows([]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(content.slice(0, MAX_SELECTION));
    } else {
      setSelectedRows([]);
    }
  };

  const onProvisionar = async () => {

    const documents = selectedRows.map(row => row.documentNumber);
    setIsLoading(true);

    await apiInstance
      .put(PROVISION_ELECTRONIC_DOCS, { documents })
      .then(response => {
        const results: { documentNumber: string; details: string }[] = response.data;
        const successCount = results.filter(r => r.details === PROVISIONED_SUCCESS).length;

        dispatch.messageModal.setMessageModal({
          show: true,
          title: `Resultado del provisionamiento (${successCount}/${results.length} exitosos)`,
          size: 'medium',
          OkTitle: 'Cerrar',
          description: (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Documento</strong></TableCell>
                  <TableCell><strong>Estado</strong></TableCell>
                  <TableCell><strong>Detalle</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map(r => {
                  const ok = r.details === PROVISIONED_SUCCESS;
                  return (
                    <TableRow key={r.documentNumber} sx={{ backgroundColor: ok ? '#f0fff4' : '#fff5f5' }}>
                      <TableCell>{r.documentNumber}</TableCell>
                      <TableCell>
                        {ok
                          ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
                          : <CancelIcon sx={{ color: '#c62828', fontSize: 18 }} />}
                      </TableCell>
                      <TableCell sx={{ color: ok ? '#2e7d32' : '#c62828', fontSize: '0.8rem' }}>
                        {ok
                          ? 'Documento provisionado exitosamente'
                          : (PROVISION_ERROR_MESSAGES[r.details.replace('DocumentProvisionErrorCode.', '')] ?? r.details.replace('DocumentProvisionErrorCode.', ''))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ),
        });

        setSelectedRows([]);
        fetchData(numberPage - 1, size); // numberPage is 1-based, fetchData expects 0-based
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  return {
    content,
    totalElements,
    numberPage,
    size,
    contentSize,
    isLoading,
    selectedRows,
    valuesResultsPages,
    fetchData,
    handleChangePage,
    handleChangeSizeRows,
    handleChangeCheck,
    handleSelectAll,
    clearSelection,
    onProvisionar,
  };
};
